import * as Cesium from 'cesium';
import { APP_CONFIG, QUALITY_SETTINGS } from '../config/appConfig';
import { DATA_SOURCES } from '../config/dataSources';
import { loadRoadDetails } from './roadDetails';

interface RoadTileJson {
  tiles?: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: [number, number, number, number];
  attribution?: string;
}

export interface RoadLoadResult {
  provider: Cesium.MVTDataProvider;
  tileset: Cesium.Cesium3DTileset;
  setVisible: (visible: boolean) => void;
}

export async function loadRoads(
  viewer: Cesium.Viewer,
  onError: (error: unknown) => void,
): Promise<RoadLoadResult> {
  try {
    let tileJson: RoadTileJson = {};
    try {
      tileJson = (await Cesium.Resource.fetchJson(
        DATA_SOURCES.roads,
      )) as RoadTileJson;
      console.info('[Road MVT] TileJSON ready', tileJson);
    } catch (error) {
      // TileJSONが一時的に取得できない場合も、取得済みの公式テンプレートで継続する。
      console.warn('[Road MVT] TileJSON fallback', error);
    }
    const template = tileJson.tiles?.[0] ?? DATA_SOURCES.roadTileTemplate;
    console.info('[Road MVT] provider start', { template });

    const bounds = tileJson.bounds ?? [
      APP_CONFIG.orthoCoverage.west,
      APP_CONFIG.orthoCoverage.south,
      APP_CONFIG.orthoCoverage.east,
      APP_CONFIG.orthoCoverage.north,
    ];
    const provider = await Cesium.MVTDataProvider.fromUrl(template, {
      minZoom: tileJson.minzoom ?? 10,
      // 公式道路MVTの最大ズームまで読み込み、近距離で道路が欠けないようにする。
      maxZoom: tileJson.maxzoom ?? 16,
      extent: Cesium.Rectangle.fromDegrees(
        bounds[0],
        bounds[1],
        bounds[2],
        bounds[3],
      ),
      heightReference: Cesium.HeightReference.CLAMP_TO_TERRAIN,
      scene: viewer.scene,
    });
    console.info('[Road MVT] provider ready');

    const tileset = provider.tileset;
    if (!tileset) throw new Error('道路MVTの内部Tilesetを生成できません');
    // 道路ポリゴンを十分な濃さで残し、航空写真の上でも欠落して見えないようにする。
    tileset.style = new Cesium.Cesium3DTileStyle({
      color: 'color("#4a4f54", 0.78)',
    });

    const settings = QUALITY_SETTINGS[APP_CONFIG.defaultQuality];
    tileset.maximumScreenSpaceError = settings.roadMaximumScreenSpaceError;
    tileset.shadows = Cesium.ShadowMode.DISABLED;
    tileset.foveatedScreenSpaceError = true;
    tileset.foveatedTimeDelay = 0.15;
    viewer.scene.primitives.add(tileset);

    let details: Cesium.GeoJsonDataSource | undefined;
    let visibleState = true;
    void loadRoadDetails(viewer).then((loaded) => {
      details = loaded;
      details.show = visibleState;
      viewer.scene.requestRender();
    }).catch((error) => console.warn('[Road details] 区画線なしで継続', error));

    const setVisible = (visible: boolean): void => {
      provider.show = visible;
      visibleState = visible;
      if (details) details.show = visible;
      viewer.scene.requestRender();
    };
    setVisible(true);
    return { provider, tileset, setVisible };
  } catch (error) {
    console.error('[Road MVT] provider initialization failed', {
      name: error instanceof Error ? error.name : undefined,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    onError(error);
    throw error;
  }
}
